import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../core/auth.service';
import { UserService } from '../users/user.service';
import { StaffDialogComponent } from './staff-dialog/staff-dialog.component';
import { StaffService } from './staff.service';

@Component({
  selector: 'app-staff',
  templateUrl: './staff.component.html',
  styleUrls: ['./staff.component.scss']
})
export class StaffComponent implements OnInit {
  displayedColumns: string[] = [
    'employeeId',
    'fullName',
    'department',
    'position',
    'phoneNumber',
    'email',
    'isActive',
    'actions'
  ];

  dataSource = new MatTableDataSource<any>([]);
  users: any[] = [];
  loading = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private service: StaffService,
    private dialog: MatDialog,
    private auth: AuthService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadStaff();
  }

  loadStaff(): void {
    this.loading = true;
    forkJoin({
      staff: this.service.getAll(),
      users: this.canManage() ? this.userService.getUsers() : of([])
    }).subscribe({
      next: ({ staff, users }) => {
        this.dataSource.data = staff || [];
        this.users = users || [];
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        Swal.fire('Error', 'Failed to load staff records.', 'error');
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  openCreate(): void {
    if (!this.canManage()) return;
    this.openDialog('create');
  }

  openEdit(staff: any): void {
    if (!this.canManage()) return;
    this.openDialog('edit', staff);
  }

  openMyProfile(): void {
    this.service.getMyProfile().subscribe({
      next: (staff) => this.openDialog('self', staff || {}),
      error: () => this.openDialog('self', {})
    });
  }

  deleteStaff(staff: any): void {
    if (!this.canManage()) return;

    Swal.fire({
      title: 'Delete Staff?',
      text: `This will remove ${staff.fullName} permanently.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.service.delete(staff.id).subscribe({
        next: () => {
          Swal.fire('Deleted', 'Staff record removed.', 'success');
          this.loadStaff();
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Delete failed.', 'error')
      });
    });
  }

  canManage(): boolean {
    const role = (this.auth.getRole() || '').toLowerCase();
    return (
      this.auth.isAdmin() ||
      role === 'hr' ||
      this.auth.hasPermission('staff.manage') ||
      this.auth.hasPermission('staff.create') ||
      this.auth.hasPermission('staff.edit') ||
      this.auth.hasPermission('staff.delete')
    );
  }

  private openDialog(mode: 'create' | 'edit' | 'self', staff?: any): void {
    const dialogRef = this.dialog.open(StaffDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: true,
      panelClass: 'app-modal-panel',
      backdropClass: 'app-modal-backdrop',
      data: { mode, staff: staff || null, users: this.users || [] }
    });

    dialogRef.afterClosed().subscribe((updated) => {
      if (updated) this.loadStaff();
    });
  }
}
